import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiJson, apiSend } from "@/api/client";
import { segment, withQuery } from "@/api/query";
import type { InvoiceEmail } from "@/api/emails";
import type { Payment } from "@/api/payments";
import { vesselKeys } from "@/api/vessels";

/*
 * Mirrors api/src/pbo_invoicing/invoices/schemas.py. Money and quantities are strings
 * ("1400.00", "1.000"); dates are YYYY-MM-DD.
 */

export type InvoiceState = "draft" | "issued" | "cancelled";

export type CreditNoteSummary = {
  id: string;
  invoice_no: string | null;
  state: InvoiceState;
  invoice_date: string;
  currency: string;
  total: string;
  reason: string;
  original_invoice_id: string;
  original_invoice_no: string | null;
  client_code: string;
};

export type CreditNoteInput = {
  /** null = today */
  invoice_date: string | null;
  reason: string;
  lines: { description: string; detail: string; amount: string }[];
};
export type DocumentKind = "original" | "regenerated" | "amended" | "imported";

/** Derived by the API from payments and dates (api/src/pbo_invoicing/domain/status.py). */
export type PaymentStatusName =
  | "draft"
  | "issued"
  | "sent"
  | "partially_paid"
  | "paid"
  | "credited"
  | "closed"
  | "cancelled";

/** List filter: a payment status, "unpaid" (issued, sent or partly paid) or "overdue". */
export type StatusFilter = PaymentStatusName | "unpaid" | "overdue";

export type SentVia = "email" | "whatsapp" | "courier" | "hand" | "portal" | "other";

export type MarkSentInput = { sent_on: string; via: SentVia; note: string };

/*
 * A line is either an item of the client's rate card, or a charge agreed for this call alone
 * and typed in - work the client asked for that the rate card does not price yet.
 */
export type LineInput =
  | {
      rate_item_id: string;
      qty: string;
      /** The wording printed for this invoice alone; left out, the rate card's. */
      description?: string;
      detail?: string;
    }
  | {
      rate_item_id?: undefined;
      qty: string;
      description: string;
      detail?: string;
      unit_rate: string;
      rate_group?: "A" | "B";
      /** Left out, it carries on from the highest number on the client's rate card. */
      item_no?: number;
    };

export function isOneOff(line: LineInput): line is Extract<LineInput, { description: string }> {
  return line.rate_item_id === undefined;
}

/** One amount off the subtotal, printed as "Less: Discount - <description>". */
export type DiscountInput = {
  description: string;
  amount: string;
  /** The numbered note about it; empty prints the standard one-time-concession wording. */
  note?: string;
};

export type InvoiceUpdateInput = {
  vessel_call_id: string;
  invoice_date: string;
  payment_days: number;
  /** null = no bank account yet: the PDF says details follow on letterhead */
  bank_account_id: string | null;
  remarks: string;
  lines: LineInput[];
  /** null or left out = no discount */
  discount?: DiscountInput | null;
  /** The director's own notes, printed after the standard ones. */
  extra_notes?: string[];
};

export type InvoiceCreateInput = InvoiceUpdateInput & {
  client_id: string;
  /** An additional invoice: the issued invoice whose vessel call it bills again. */
  additional_to_id?: string | null;
};

export type AmendInput = {
  reason: string;
  voyage: string;
  terminal: string;
  port: string;
  arrival: string;
  departure: string;
  service_from: string;
  service_to: string;
  /** Left out: unchanged. Only on an invoice with a discount. */
  discount_note?: string;
  /** Left out: unchanged. */
  extra_notes?: string[];
};

export type InvoiceLine = {
  line_no: number;
  rate_item_id: string | null;
  item_no: number;
  rate_group: "A" | "B";
  description: string;
  detail: string;
  qty: string;
  unit_rate: string;
  amount: string;
};

export type InvoiceDocument = {
  version: number;
  kind: DocumentKind;
  sha256: string;
  byte_len: number;
  renderer_version: string;
  font_family: string;
  snapshot_version: number;
  reason: string;
  created_by_username: string | null;
  created_at: string;
};

export type PrintedVesselCall = {
  vessel: string;
  imo: string;
  voyage: string;
  terminal: string;
  port: string;
  arrival: string;
  departure: string;
  service_from: string;
  service_to: string;
};

export type PrintedBank = {
  account_name: string;
  bank: string;
  branch: string;
  account_no: string;
  swift: string;
  ifsc: string;
  iban: string;
};

/** The frozen snapshot of an issued invoice; only the parts the app shows are typed. */
export type InvoiceSnapshot = {
  invoice_no: string;
  vessel_call: PrintedVesselCall;
  bank: PrintedBank | null;
  discount?: DiscountInput | null;
  extra_notes?: string[];
};

export type InvoiceSummary = {
  id: string;
  doc_type: string;
  state: InvoiceState;
  invoice_no: string | null;
  invoice_date: string;
  due_date: string;
  client_id: string;
  client_code: string;
  client_name: string;
  vessel_call_id: string | null;
  /** An additional invoice: the issued invoice whose vessel call it bills again. */
  additional_to_id: string | null;
  vessel_name: string | null;
  voyage: string | null;
  departure: string | null;
  currency: string;
  subtotal: string;
  discount_amount: string;
  tax_amount: string;
  total: string;
  source: "app" | "legacy_import";
  issued_at: string | null;
  cancelled_at: string | null;
  status: PaymentStatusName;
  paid_amount: string;
  balance: string;
  /** null while an invoice issued without bank details waits for the bank letter */
  effective_due_date: string | null;
  awaiting_bank_letter: boolean;
  is_overdue: boolean;
  days_overdue: number;
  sent_on: string | null;
  closed_at: string | null;
  version: number;
  created_at: string;
  updated_at: string;
};

export type Invoice = InvoiceSummary & {
  payment_days: number;
  tax_mode: string;
  bank_account_id: string | null;
  remarks: string;
  discount_description: string;
  discount_note: string;
  extra_notes: string[];
  fy_code: string | null;
  serial: number | null;
  snapshot_version: number;
  snapshot: InvoiceSnapshot | null;
  issued_by_username: string | null;
  cancelled_by_username: string | null;
  cancel_reason: string | null;
  created_by_username: string | null;
  current_document_version: number | null;
  sent_via: SentVia | null;
  sent_note: string;
  closed_by_username: string | null;
  close_reason: string | null;
  write_off_amount: string;
  lines: InvoiceLine[];
  documents: InvoiceDocument[];
  payments: Payment[];
  emails: InvoiceEmail[];
  /** A credit note names the invoice it reduces; an invoice lists its credit notes. */
  original_invoice_id: string | null;
  original_invoice_no: string | null;
  additional_to_no: string | null;
  credit_notes: CreditNoteSummary[];
  /** The total of its issued credit notes. */
  credited: string;
};

export type SeriesIntegrity = {
  doc_type: string;
  fy_code: string;
  prefix: string;
  first_serial: number;
  next_serial: number;
  numbers_used: number;
  missing_serials: number[];
  unexpected_serials: number[];
};

export type IntegrityReport = {
  ok: boolean;
  checked_at: string;
  series: SeriesIntegrity[];
  invoices_checked: number;
  documents_checked: number;
  receipts_checked: number;
  receipt_documents_checked: number;
  problems: string[];
};

export type InvoiceFilters = {
  state?: InvoiceState;
  status?: StatusFilter;
  q?: string;
  clientId?: string;
  fy?: string;
  limit?: number;
};

export type PdfFile = { blob: Blob; filename: string; sha256: string | null };

export const invoiceKeys = {
  all: ["invoices"] as const,
  lists: ["invoices", "list"] as const,
  list: (filters: InvoiceFilters) => ["invoices", "list", filters] as const,
  detail: (id: string) => ["invoices", "detail", id] as const,
  integrity: ["invoices", "integrity"] as const,
};

/** The stored file name, as the API names it: "PBO-26-27-0101_DEDE_KORKUT.pdf" (then _v2). */
export function documentFileName(
  invoice: Pick<InvoiceSummary, "invoice_no" | "vessel_name">,
  version: number,
): string {
  const vessel = (invoice.vessel_name ?? "").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  const stem = (invoice.invoice_no ?? "draft").replace(/\//g, "-");
  const name = vessel ? `${stem}_${vessel}` : stem;
  return version > 1 ? `${name}_v${version}.pdf` : `${name}.pdf`;
}

/** "PBO/26-27/0102" from a series' prefix, year and serial. */
export function formatInvoiceNumber(prefix: string, fyCode: string, serial: number): string {
  return `${prefix}/${fyCode}/${String(serial).padStart(4, "0")}`;
}

// --------------------------------------------------------------------------- requests

export function listInvoices(filters: InvoiceFilters = {}): Promise<InvoiceSummary[]> {
  return apiJson<InvoiceSummary[]>(
    withQuery("/invoices", {
      state: filters.state,
      status: filters.status,
      q: filters.q,
      client_id: filters.clientId,
      fy: filters.fy,
      limit: filters.limit,
    }),
  );
}

export function getInvoice(id: string): Promise<Invoice> {
  return apiJson<Invoice>(`/invoices/${segment(id)}`);
}

export function createInvoice(input: InvoiceCreateInput): Promise<Invoice> {
  return apiSend<Invoice>("/invoices", "POST", input);
}

export function updateInvoice(
  id: string,
  input: InvoiceUpdateInput,
  version: number,
): Promise<Invoice> {
  return apiSend<Invoice>(`/invoices/${segment(id)}`, "PUT", input, version);
}

export function deleteInvoice(id: string, version: number): Promise<void> {
  return apiSend<void>(`/invoices/${segment(id)}`, "DELETE", undefined, version);
}

export function issueInvoice(id: string, version: number): Promise<Invoice> {
  return apiSend<Invoice>(`/invoices/${segment(id)}/issue`, "POST", undefined, version);
}

export function amendInvoice(id: string, input: AmendInput, version: number): Promise<Invoice> {
  return apiSend<Invoice>(`/invoices/${segment(id)}/amend`, "POST", input, version);
}

export function regenerateDocument(id: string, reason: string, version: number): Promise<Invoice> {
  return apiSend<Invoice>(
    `/invoices/${segment(id)}/documents/regenerate`,
    "POST",
    { reason },
    version,
  );
}

export function cancelInvoice(id: string, reason: string, version: number): Promise<Invoice> {
  return apiSend<Invoice>(`/invoices/${segment(id)}/cancel`, "POST", { reason }, version);
}

export function markSent(id: string, input: MarkSentInput, version: number): Promise<Invoice> {
  return apiSend<Invoice>(`/invoices/${segment(id)}/mark-sent`, "POST", input, version);
}

export function closeInvoice(id: string, reason: string, version: number): Promise<Invoice> {
  return apiSend<Invoice>(`/invoices/${segment(id)}/close`, "POST", { reason }, version);
}

export function reopenInvoice(id: string, reason: string, version: number): Promise<Invoice> {
  return apiSend<Invoice>(`/invoices/${segment(id)}/reopen`, "POST", { reason }, version);
}

export async function fetchPreviewPdf(id: string): Promise<Blob> {
  const res = await apiFetch(`/invoices/${segment(id)}/preview.pdf`);
  return res.blob();
}

export async function fetchDocumentPdf(
  id: string,
  version: number,
  fallbackName = `invoice-v${version}.pdf`,
): Promise<PdfFile> {
  const res = await apiFetch(`/invoices/${segment(id)}/documents/${version}.pdf`);
  const match = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "");
  return {
    blob: await res.blob(),
    filename: match?.[1] ?? fallbackName,
    sha256: res.headers.get("X-Content-SHA256"),
  };
}

export function getIntegrityReport(): Promise<IntegrityReport> {
  return apiJson<IntegrityReport>("/admin/integrity");
}

// --------------------------------------------------------------------------- hooks

export function useInvoices(filters: InvoiceFilters) {
  return useQuery({ queryKey: invoiceKeys.list(filters), queryFn: () => listInvoices(filters) });
}

export function useInvoice(id: string, enabled = true) {
  return useQuery({ queryKey: invoiceKeys.detail(id), queryFn: () => getInvoice(id), enabled });
}

export function useIntegrityReport() {
  return useQuery({ queryKey: invoiceKeys.integrity, queryFn: getIntegrityReport });
}

/** A draft credit note against an issued invoice; resolves with the draft. */
export function createCreditNote(invoiceId: string, input: CreditNoteInput): Promise<Invoice> {
  return apiSend<Invoice>(`/invoices/${segment(invoiceId)}/credit-notes`, "POST", input);
}

export function useCreateCreditNote(invoiceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreditNoteInput) => createCreditNote(invoiceId, input),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) }),
        queryClient.invalidateQueries({ queryKey: invoiceKeys.lists }),
      ]),
  });
}

function useInvoiceMutation<Variables>(mutationFn: (variables: Variables) => Promise<Invoice>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (invoice) => {
      queryClient.setQueryData(invoiceKeys.detail(invoice.id), invoice);
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: invoiceKeys.lists }),
        queryClient.invalidateQueries({ queryKey: invoiceKeys.integrity }),
        // Amendments correct the vessel call too.
        queryClient.invalidateQueries({ queryKey: vesselKeys.calls }),
      ]);
    },
  });
}

export function useCreateInvoice() {
  return useInvoiceMutation((input: InvoiceCreateInput) => createInvoice(input));
}

export function useUpdateInvoice(id: string) {
  return useInvoiceMutation(({ input, version }: { input: InvoiceUpdateInput; version: number }) =>
    updateInvoice(id, input, version),
  );
}

export function useIssueInvoice(id: string) {
  return useInvoiceMutation((version: number) => issueInvoice(id, version));
}

export function useAmendInvoice(id: string) {
  return useInvoiceMutation(({ input, version }: { input: AmendInput; version: number }) =>
    amendInvoice(id, input, version),
  );
}

export function useRegenerateDocument(id: string) {
  return useInvoiceMutation(({ reason, version }: { reason: string; version: number }) =>
    regenerateDocument(id, reason, version),
  );
}

export function useCancelInvoice(id: string) {
  return useInvoiceMutation(({ reason, version }: { reason: string; version: number }) =>
    cancelInvoice(id, reason, version),
  );
}

export function useMarkSent(id: string) {
  return useInvoiceMutation(({ input, version }: { input: MarkSentInput; version: number }) =>
    markSent(id, input, version),
  );
}

export function useCloseInvoice(id: string) {
  return useInvoiceMutation(({ reason, version }: { reason: string; version: number }) =>
    closeInvoice(id, reason, version),
  );
}

export function useReopenInvoice(id: string) {
  return useInvoiceMutation(({ reason, version }: { reason: string; version: number }) =>
    reopenInvoice(id, reason, version),
  );
}

export function useDeleteInvoice(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (version: number) => deleteInvoice(id, version),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: invoiceKeys.detail(id) });
      return queryClient.invalidateQueries({ queryKey: invoiceKeys.lists });
    },
  });
}
